import { CloseIcon } from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Flex,
    HStack,
    IconButton,
    Text,
    VStack,
} from '@chakra-ui/react';
import React from 'react';
import FilterActiveIcon from '../../assets/icons/filter-active.svg';

interface FilterStateIndicatorProps {
  appliedFilters: Record<string, string | string[]>;
  totalCount: number;
  filteredCount: number;
  resourceType: 'providers' | 'practices' | 'plans';
  onClearFilters: () => void;
  marketOptions?: Array<{ label: string; value: string }>;
  stateOptions?: Array<{ label: string; value: string }>;
}

const FilterStateIndicator: React.FC<FilterStateIndicatorProps> = ({
  appliedFilters,
 // totalCount,
 // filteredCount,
  resourceType,
  onClearFilters,
  marketOptions = [],
  stateOptions = [],
}) => {
  const filterCount = Object.keys(appliedFilters).length;

  if (filterCount === 0) {
    return null;
  }

  const getFilterDisplayName = (key: string): string => {
    const displayNames: Record<string, string> = {
      marketId: 'Market',
      marketIds: 'Market',
      state: 'State',
      states: 'States',
      statuses: 'Status',
      payorNames: 'Payor',
      q: 'Search',
    };
    return displayNames[key] || key;
  };

  const getFilterDisplayValue = (
    key: string,
    value: string | string[]
  ): string => {
    if (Array.isArray(value)) {
      if (value.length === 0) return '';

      // For marketId/marketIds arrays, show friendly market labels
      if ((key === 'marketId' || key === 'marketIds') && marketOptions.length > 0) {
        const labels = value
          .slice(0, 2)
          .map((v) => {
            const market = marketOptions.find(
              (option) => option.value === v
            );
            return market ? market.label : v;
          });

        if (value.length > 2) {
          return `${labels.join(', ')} +${value.length - 2} more`;
        }

        return labels.join(', ');
      }

      // For states arrays, show friendly state labels
      if (key === 'states' && stateOptions.length > 0) {
        const labels = value
          .slice(0, 2)
          .map((v) => {
            const state = stateOptions.find(
              (option) => option.value === v
            );
            return state ? state.label : v;
          });

        if (value.length > 2) {
          return `${labels.join(', ')} +${value.length - 2} more`;
        }

        return labels.join(', ');
      }

      // Generic array fallback
      return value.length === 1 ? value[0] : `${value.length} selected`;
    }

    // Handle market display (singular)
    if ((key === 'marketId' || key === 'marketIds') && marketOptions.length > 0) {
      const market = marketOptions.find((option) => option.value === value);
      return market ? market.label : value || '';
    }

    // Handle state display (singular)
    if ((key === 'state' || key === 'states') && stateOptions.length > 0) {
      const state = stateOptions.find((option) => option.value === value);
      return state ? state.label : value || '';
    }

    return value || '';
  };

  return (
    <Box
      bg="blue.50"
      border="1px solid"
      borderColor="blue.200"
      borderRadius="0.5rem"
      p="1rem"
      mb="1rem"
    >
      <Flex justify="space-between" align="flex-start" gap="1rem">
        <HStack spacing="0.75rem" align="flex-start">
          <Box flexShrink={0} mt="0.125rem">
            <img
              src={FilterActiveIcon}
              alt="Active filters"
              width="24"
              height="24"
            />
          </Box>

          <VStack align="flex-start" spacing="0.5rem" flex="1">
            <HStack spacing="0.5rem" align="center">
              <Text
                fontSize="0.875rem"
                fontWeight="600"
                color="blue.700"
                lineHeight="1.25rem"
              >
                {filterCount} filter{filterCount === 1 ? '' : 's'} applied
              </Text>
              <Text
                fontSize="0.875rem"
                color="blue.600"
                lineHeight="1.25rem"
              >
                •
              </Text>
              <Text
                fontSize="0.875rem"
                color="blue.600"
                lineHeight="1.25rem"
              >
                Filtered {resourceType}
              </Text>
            </HStack>

            <Flex wrap="wrap" gap="0.5rem">
              {Object.entries(appliedFilters).map(([key, value]) => (
                <Badge
                  key={key}
                  variant="solid"
                  colorScheme="blue"
                  fontSize="0.75rem"
                  px="0.75rem"
                  py="0.375rem"
                  borderRadius="0.375rem"
                >
                  {getFilterDisplayName(key)}: {getFilterDisplayValue(key, value)}
                </Badge>
              ))}
            </Flex>
          </VStack>
        </HStack>

        <IconButton
          aria-label="Clear all filters"
          icon={<CloseIcon />}
          size="sm"
          variant="ghost"
          colorScheme="blue"
          onClick={onClearFilters}
          _hover={{
            bg: 'blue.100',
          }}
        />
      </Flex>
    </Box>
  );
};

export default FilterStateIndicator;
